import { CSSProperties, useCallback } from "react";
import { useAtomValue } from "jotai";
import { submenuDataAtom, syncServiceAtom, transformerRefAtom, undoRedoStackAtom, utilsAtom } from "state-atoms";
import consts from "shared/consts";
import tracking from "frontend/tracking";
import style from "frontend/canvas-designer-new/elements-toolbar/elements-toolbar.module.css";
import popupStyle from "frontend/canvas-designer-new/element-dynamic-data/toolbar/customize-monday-item.module.css";
import { MondayLogo } from "frontend/icons/monday-logo";
import { DropdownButton } from "frontend/canvas-designer-new/elements-toolbar/toolbar-buttons";
import { CustomizeMondayItemIcons } from "frontend/canvas-designer-new/element-dynamic-data/toolbar/customize-monday-item-icons";
import { placeStickyNote } from "shared/datamodel/sticky-note";
import { placeShape } from "shared/datamodel/shape";
import { getDefaultSizeForShape } from "utils/shape-utils";
import { MondayTaskElement } from "../schema";
import { Connector, Shape, StickyNote } from "shared/datamodel/schemas";
import { canvasElementPrefix } from "shared/util/utils";
import { ReadTransaction } from "@workcanvas/reflect";
import { useDynamicElementActions } from "frontend/canvas-designer-new/element-dynamic-data/hooks/use-dynamic-element-actions";
import { DynamicElement } from "frontend/canvas-designer-new/element-dynamic-data/dynamic-data-utils";
import { Sizes } from "frontend/data/shapes/sizes";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";

function CustomizeMondayTaskPopup({ newStyle, onSelect }: { newStyle: boolean; onSelect: (target: string) => void }) {
  return (
    <div
      className={popupStyle.popup}
      style={
        newStyle
          ? ({ "--bg-color": "#1e1e1e", "--border-radius": "10px", "--hover-color": "#393939" } as CSSProperties)
          : undefined
      }
    >
      <div className={popupStyle.popupHeader}>
        <div className={popupStyle.popupTitle}>
          <MondayLogo size={22} />
          Customize look
        </div>
        <div className={popupStyle.popupDescription}>
          Restyle the element,
          <br />
          keep the content
        </div>
      </div>
      <div className={popupStyle.popupIcons}>
        {Object.entries(CustomizeMondayItemIcons).map(([key, icon]) => (
          <button className={popupStyle.popupIcon} key={key} onClick={() => onSelect(key)}>
            {icon}
          </button>
        ))}
      </div>
    </div>
  );
}

export function MondayTaskCustomizeButton({
  element,
  elementId,
  documentId,
}: {
  element: MondayTaskElement;
  elementId: string;
  documentId: string;
}) {
  const syncService = useAtomValue(syncServiceAtom);
  const reflect = syncService?.getReplicache();
  const canvasUtils = useAtomValue(utilsAtom(documentId));
  const undoRedoStack = useAtomValue(undoRedoStackAtom);
  const transformerRef = useAtomValue(transformerRefAtom);
  const submenuData = useAtomValue(submenuDataAtom);
  const { addFieldToElement, addIntegrationToElement } = useDynamicElementActions();
  const newFloaters = useFeatureFlag("cim-new-floaters-style");
  const isCIM = useIsCIMReorderEnabled();
  const newFloatersStyle = isCIM && newFloaters;

  const migrateConnectors = useCallback(
    async (from: string, to: string, sequenceId?: string) => {
      const connectorIds: string[] =
        (await reflect?.query(async function (tx: ReadTransaction) {
          const prefix = `${canvasElementPrefix}connector-`;
          let allConnectors = (await tx.scan({ prefix }).entries().toArray()) as [string, Connector][];
          allConnectors = allConnectors.filter(([_, connector]) => {
            if (connector?.hidden) return false;

            const [source, destination] = connector.connectedShapes;
            if (source?.id === from || destination?.id === from) return true;
            return false;
          });

          const ids = allConnectors.map(([id]) => id.replace(canvasElementPrefix, ""));
          return ids;
        })) ?? [];

      await undoRedoStack?.patchElement(
        from,
        (element_) => {
          const { attachedConnectors } = element_;
          if (!attachedConnectors) return;

          for (const connector of connectorIds) {
            delete attachedConnectors[connector];
          }
        },
        sequenceId
      );

      await undoRedoStack?.patchElement(
        to,
        (element_) => {
          const { attachedConnectors } = element_;
          if (!attachedConnectors) return;

          for (const connector of connectorIds) {
            attachedConnectors[connector] = {
              lineId: connector,
            };
          }
        },
        sequenceId
      );

      await undoRedoStack?.patchElements(
        connectorIds,
        (connector: Connector) => {
          const [source, destination] = connector.connectedShapes;
          if (source?.id === from) {
            source.id = to;
          }
          if (destination?.id === from) {
            destination.id = to;
          }
        },
        sequenceId
      );
    },
    [reflect, undoRedoStack]
  );

  const handleCreateDataLayerElement = useCallback(
    async (target: string) => {
      const { integrationId, mondayItemId, x, y, width, height } = element;

      tracking.trackAnalyticsEvent("data_layer_created_from_monday_task", {
        integrationId,
        itemId: mondayItemId,
        target,
      });

      let targetElement: { id: string; type: string; element: Shape | StickyNote };
      const sizes = Sizes.get();
      if (target === consts.CANVAS_ELEMENTS.STICKY_NOTE) {
        const height = sizes.stickyNote.height;
        const { id, stickyNote } = placeStickyNote(
          { ...submenuData?.stickyNote },
          { x: x + height / 2, y: y + height / 2 },
          sizes.stickyNote
        );
        targetElement = { id, type: consts.CANVAS_ELEMENTS.STICKY_NOTE, element: stickyNote };
      } else {
        const shapeDefaultSize = getDefaultSizeForShape(consts.CANVAS_ELEMENTS.SHAPE, target);
        const { id, shape } = placeShape({
          point: { x: x + width / 2, y: y + height / 2 },
          init: { ...submenuData?.shape, type: consts.CANVAS_ELEMENTS.SHAPE, subtype: target },
          actualSize: shapeDefaultSize,
          defaultShapeSize: shapeDefaultSize,
          rotate: 0,
          sizes: {
            fontSize: sizes.shape.fontSize,
            strokeWidth: sizes.shape.strokeWidth.default,
            defaultRadius: sizes.shape.defaultRadius,
          },
        });
        targetElement = { id, type: consts.CANVAS_ELEMENTS.SHAPE, element: shape };
      }

      const sequenceId = `customize-monday-task-${elementId}`;
      undoRedoStack?.startSequence(sequenceId);

      const newElementIds = await canvasUtils?.onAddElements(
        [targetElement],
        true,
        "customize-monday-task",
        sequenceId
      );

      if (!newElementIds) {
        // Should never happen
        undoRedoStack?.endSequence(sequenceId);
        return;
      }

      const targetElementId = newElementIds[0];

      await addIntegrationToElement(targetElementId, integrationId, mondayItemId, {
        clearText: true,
        sequenceId,
      });

      const addedElementId = await addFieldToElement(
        targetElementId,
        targetElement.element as DynamicElement,
        integrationId,
        mondayItemId,
        "name",
        {
          ...submenuData?.textBlock,
          sequenceId,
        }
      );

      if (!addedElementId) {
        // Should never happen
        undoRedoStack?.endSequence(sequenceId);
        return;
      }

      await migrateConnectors(elementId, targetElementId, sequenceId);

      await canvasUtils?.removeCanvasElements([elementId], true, sequenceId);
      undoRedoStack?.endSequence(sequenceId);
      canvasUtils?.setSelectedElementIds([targetElementId, addedElementId]);
    },
    [
      element,
      elementId,
      canvasUtils,
      undoRedoStack,
      submenuData?.stickyNote,
      submenuData?.shape,
      submenuData?.textBlock,
      addIntegrationToElement,
      addFieldToElement,
      migrateConnectors,
    ]
  );

  const handleClick = useCallback(
    async (target: string) => {
      if (target === "integrationItem") {
        return; // We are already an "integrationItem", kinda
      }

      await handleCreateDataLayerElement(target);

      // In some cases the transformer is not updated properly,
      // we combat this by adding a timeout to enforce the update
      setTimeout(() => {
        transformerRef?.current?.forceUpdate();
      }, 100);
    },
    [handleCreateDataLayerElement, transformerRef]
  );

  return (
    <div className={style.row}>
      <DropdownButton
        data-testid="customizeMondayTask"
        tooltip="Customize look"
        hideArrow
        renderPopup={() => <CustomizeMondayTaskPopup newStyle={newFloatersStyle} onSelect={handleClick} />}
      >
        <div className={style.convertToMondayButtonContent}>
          <MondayLogo size={22} />
          <span>Customize</span>
        </div>
      </DropdownButton>
    </div>
  );
}
